import { AnimatePresence } from "motion/react";
import { useEffect, useState } from "react";
import CCCVV from "../Cvv";
import CreditCardManager from "../support/CreditCardManager";
import Tokenized from "../Tokenized";
import CCAddress from "./Address";
import CCExpiration from "./Expiration";
import CCName from "./Name";
import CCNumber from "./Number";
import CCPostalCode from "./PostalCode";

interface PaymentProps {
  className?: string;
  cvv?: string;
  fieldPrefix?: string;
  hideCvv?: boolean;
  tokenizedCard?: be.TokenizedCreditCard;
  notRequired?: boolean;
}

function CreditCard({
  className = "",
  cvv,
  fieldPrefix = "",
  hideCvv,
  tokenizedCard,
  notRequired,
}: PaymentProps): JSX.Element | null {
  const [ccMgr, setCcMgr] = useState<CreditCardManager | null>(null);
  const hasCard = Object.keys(tokenizedCard || {}).length > 0;

  // Set up cc manager
  useEffect(() => {
    async function setupManager() {
      const manager = await (async function () {
        const mgr = new CreditCardManager();
        await mgr.init();
        return mgr;
      })();

      setCcMgr(manager);
    }
    setupManager();
  }, []);

  function ccForm(ccMgr: CreditCardManager): JSX.Element {
    return (
      <>
        <CCNumber
          className=""
          errorMessage={notRequired ? "" : ccMgr.error("number")}
          errorInvalid={ccMgr.error("number", "Invalid")}
          errorType={ccMgr.error("number", "Type")}
          fieldName={`${fieldPrefix}number`}
          label={ccMgr.label("number")}
          invalidTypes={ccMgr.invalidCcTypes()}
          requiredPlaceholder={ccMgr.requiredPlaceholder()}
        />

        <CCExpiration
          className=""
          errorMessage={notRequired ? "" : ccMgr.error("expiration")}
          fieldName={`${fieldPrefix}expiration`}
          label={ccMgr.label("expiration")}
        />

        <CCName
          className=""
          errorMessage={notRequired ? "" : ccMgr.error("name")}
          fieldName={`${fieldPrefix}name`}
          label={ccMgr.label("name")}
          requiredPlaceholder={ccMgr.requiredPlaceholder()}
        />

        {ccMgr.show("address") && (
          <CCAddress
            className=""
            errorMessage={notRequired ? "" : ccMgr.error("address")}
            fieldName={`${fieldPrefix}address`}
            label={ccMgr.label("address")}
            requiredPlaceholder={ccMgr.requiredPlaceholder()}
          />
        )}

        {ccMgr.show("postalCode") && (
          <CCPostalCode
            className=""
            errorMessage={notRequired ? "" : ccMgr.error("postalCode")}
            fieldName={`${fieldPrefix}postalCode`}
            label={ccMgr.label("postalCode")}
            requiredPlaceholder={ccMgr.requiredPlaceholder()}
          />
        )}
      </>
    );
  }

  return !ccMgr ? null : (
    <div className={`rounded-lg grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-8 ${className}`}>
      {hasCard ? <Tokenized tokenizedCard={tokenizedCard} /> : ccForm(ccMgr)}

      <AnimatePresence>
        {!hideCvv && ccMgr.show("cvv") && !cvv && (
          <CCCVV
            key={cvv}
            className="sm:col-span-2"
            errorMessage={notRequired ? "" : ccMgr.error("cvv")}
            fieldName={`${fieldPrefix}cvv`}
            label={ccMgr.label("cvv")}
            requiredPlaceholder={ccMgr.requiredPlaceholder()}
            value={cvv}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default CreditCard;
